import { useEffect, useRef, useState } from 'react';
import ASCScale from './contents/ASCScale';
import SatCld from './contents/SatCld';
import ModelUKMET from './contents/ModelUKMET';
import {formatUTCDate, parseUTCDate, deepCopy, isEqualObject, isReverseRoute} from '../../utils/FPEUtils';
import CrossSection from 'CrossSection';
import CrossLegendPanel from './CrossLegendPanel';
import airPlaneOrange from '../../images/contents/crosssection/airplane_orange.png';
import airPlaneAliceBlue from '../../images/contents/crosssection/airplane_alice_blue.png';
import EmphasizeCrossSection from './contents/EmphasizeCrossSection';
import CrossSectionConf from './CrossSectionConf';
import ACOS from './contents/ACOS';
import { useCsColorState } from "../../store/CrossSectionColorState";
import { useATCPlan } from "../../store/ATCPlanState";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { highlightPointsState } from "../../store/HighlightPointsState";
import { elapsedTimeForWeatherState, elapsedTimeState } from "../../store/ElapsedTimeState";
import { selectedWeatherList } from '../../store/WeatherContentsState';
import { addCsLegendSelector, removeCsLegendSelector } from '../../store/CsLegendState';
import { csRevision } from '../../store/CsRevisionState';
import CsModeChangePanel from './CsModeChangePanel';
import FlightPathModeState from '../../store/FlightPathModeState';
import ToggleValidTime from './ToggleValidTime';
import ZoomControlPanel from './ZoomControlPanel';
import { csValidTimeVisibleState } from '../../store/CsValidTimeVisibleState';
import { useCsZoomState } from "../../store/CsZoomState";
import WrapOption from "../../constants/WrapOption";
import { makeStyles, CircularProgress } from "@material-ui/core";
import { headerVisibleState } from "../../store/HeaderVisibleState";

const crossContents = {};
let _elapsedTimeForWeather;
let _elapsedTime;
let workerMode = false;

function FPECrossSection({crossSectionSize}) {
  return (
    <>
      <Cs crossSectionSize={crossSectionSize}/>
      <CsAirplane />
      <EmphasizeCrossSectionWrapper />
      <CrossLegendPanel />
      <CsModeChangePanel />
      <ToggleValidTime />
      <DrawingProgress />
      <ZoomControlPanel />
    </>
  )
}

const useStyles = makeStyles(() => ({
  progressRoot: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translateY(-50%) translateX(-50%)",
    pointerEvents: "none"
  },
  progress: {
    color: "#1a90ff",
  }
}));

let hiddenProgress;
function DrawingProgress() {

  const classes = useStyles();
  const [hidden, func] = useState(true);
  hiddenProgress = func;

  return (
    <div className={classes.progressRoot} hidden={hidden}>
      <CircularProgress size={30} thickness={5} className={classes.progress} />
    </div>
  );
}

function EmphasizeCrossSectionWrapper() {
  const highlightPoints = useRecoilValue(highlightPointsState);
  const [emphasizeOpt, setEmphasizeOpt] = useState({fixes:[]});

  useEffect( () => {
    let didCancel = false;
    (async()=>{
      const opt = await createEmphasizeOption(highlightPoints);
      if (!didCancel) {
        setEmphasizeOpt(opt);
      }
    })();

    return () => {
      didCancel = true;
    };
  },[highlightPoints]);

  return <EmphasizeCrossSection emphasizeOpt={emphasizeOpt} ></EmphasizeCrossSection>;
}

const yOffset = 7;
const xOffset = 0;

function CsAirplane() {
  const {csColor} = useCsColorState();
  const [airPlane, setAirPlane] = useState(airPlaneOrange);
  const elapsedTime = useRecoilValue(elapsedTimeState);
  const [highlightPoints, setHighlightPoints] = useRecoilState(highlightPointsState);

  useEffect(() => {
    _elapsedTime = elapsedTime;
    updateAirPlaneIcon();
  },[elapsedTime]);

  useEffect(() => {
    if(csColor === "black") {
      setAirPlane(airPlaneAliceBlue);
    } else {
      setAirPlane(airPlaneOrange);
    }
  },[csColor]);

  const sendAirplaneHighlight = (event => {
    let data = [];
    if(event.type === 'mouseover') {
      data.push({
        source: 'crosssection',
        type: 'plane',
        name: 'AirPlane',
        lon: null,
        lat: null,
        index: -1
      });
    }
    setHighlightPoints(data);
  });

  let sonarStyle = "";
  if(highlightPoints.length === 1
    && highlightPoints[0].source !== 'crosssection'
    && highlightPoints[0].type === 'plane') {
    sonarStyle = "cs-airplane-sonar";
  }

  return (
    <div id="airplaneIcon" className={`airplane-cushion-sonar ${sonarStyle}`}>
      <img id="airplaneImage" className="airplane-icon-sonar" src={airPlane} alt="airplaneIcon" onMouseOver={sendAirplaneHighlight} onMouseOut={sendAirplaneHighlight}></img>
    </div>
  );
}

function Cs({crossSectionSize}) {
  const {atcPlan} = useATCPlan();
  const {csColor} = useCsColorState();
  const setHighlightPoints = useSetRecoilState(highlightPointsState);
  const elapsedTimeForWeather = useRecoilValue(elapsedTimeForWeatherState);
  const highlightPoint = useRef([]);

  const selectedWeather = useRecoilValue(selectedWeatherList);
  const addCsLegend = useSetRecoilState(addCsLegendSelector);
  const removeCsLegend = useSetRecoilState(removeCsLegendSelector);
  const [revision, setRevision] = useRecoilState(csRevision);
  const flightPathMode = useRecoilValue(FlightPathModeState);
  const csValidTimeVisible = useRecoilValue(csValidTimeVisibleState);
  const headerVisible = useRecoilValue(headerVisibleState);
  const {isXZoomEnable, isYZoomEnable} = useCsZoomState();

  // マウント/アンマウント時に動作する
  useEffect(() => {
    const onMouseOver = touched => {
      if(highlightPoint.current === touched._route_index) {
        return;
      }

      highlightPoint.current = touched._route_index

      let type;
      let routeIndex;
      let airwayFixes = [];
      if(!Array.isArray(touched._route_index)) {
        routeIndex = [touched._route_index];
        type = "fix";
      } else {
        routeIndex = touched._route_index;
        type = "route";

        const routeWithAirway = parsedAtcPlan["routeWithAirway"];
        // 選択したルートの終端のFix
        const lastFix = routeWithAirway.find((fix) => {
          return fix[0] === touched._chart._route[routeIndex[routeIndex.length - 1]][0];
        });
        // 選択したルートの先端のFIXと終端のFIXがAirwayでありかつ同一であるか
        const isMatchedAirway = routeWithAirway.some((fix) => {
          return fix[0] === touched._chart._route[routeIndex[0]][0] && fix[3] && fix[3] === lastFix[3] && fix[4] === lastFix[4];
        });

        if(isMatchedAirway) {
          type = "airway";

          // 該当のairwayを持つFIXに絞る
          for(let i = 0; i < routeWithAirway.length; i++) {
            const fix = routeWithAirway[i];
            if(fix[3] === lastFix[3] && fix[4] === lastFix[4]) {
              airwayFixes.push({name: fix[0], lon: fix[2], lat: fix[1], source: 'crosssection', type: type});
            }
          }
        }
      }

      let data = [];
      if(type === "airway") {
        data = airwayFixes;
      } else {
        routeIndex.forEach((idx) => {
          const fix = touched._chart._route[idx];
          data.push({name: fix[0], lon: fix[2], lat: fix[1], index: idx, source: 'crosssection', type: type});
        });
      } 
  
      setHighlightPoints(data);
    }
    const onMouseOut = () => {
      if(highlightPoint.current === null) {
        return;
      }
      highlightPoint.current = null;
      setHighlightPoints([]);
    }
    initCrossSection(onMouseOver, onMouseOut, setRevision);
  },[setHighlightPoints, setRevision]);

  // 気象コンテンツのON/OFF時に動作する
  useEffect(() => {
    const setContentsState = (weatherState) => {
      Object.keys(crossContents).forEach(key => {
        const visible = weatherState.includes(key);
        const contents = crossContents[key];
        if(visible && !contents.isVisible()) {
          addCsLegend(contents.getLegend());
        } else if(!visible && contents.isVisible()){
          removeCsLegend(contents.getLegend());
        }
        if (visible !== contents.isVisible()) {
          contents.setVisible(visible);
        }
      });
    }
    setContentsState(selectedWeather);
    updateCrossSection();
  },[selectedWeather, addCsLegend, removeCsLegend]);

  useEffect(() => {
    updateCrossSection();
  },[revision]);

  useEffect(() => {
    _fpMode = flightPathMode;
    if(!flightPathMode) {
      csController.enableTimeLabel(false);
    } else {
      csController.enableTimeLabel(csValidTimeVisible);
    }
    updateCrossSection();
  },[flightPathMode, csValidTimeVisible]);

  useEffect(() => {
    csController.enableXZoom(isXZoomEnable);
  },[isXZoomEnable]);

  useEffect(() => {
    csController.enableYZoom(isYZoomEnable);
  },[isYZoomEnable]);

  // atcPlanのjson変更時に動作する
  useEffect(() => {
    parseAtcPlan(atcPlan.edit.json);
    updateCrossSection();
  },[atcPlan.edit.json]);

  // タイムスライダーで時制変更時に動作する
  useEffect(() => {
    _elapsedTimeForWeather = elapsedTimeForWeather;
    if(!_fpMode) {
      updateCrossSection();
    } else {
      updateAirPlaneIcon();
    }
    
  },[elapsedTimeForWeather]);

  // 断面図のサイズ変更時に動作する
  useEffect(() => {
    resizeCrossSection();
  },[crossSectionSize, headerVisible]);

  useEffect(() => {
    setCrossSectionColor(csColor);
  },[csColor]);

  return (
    <div id="cross-section" className="cross-section" height={crossSectionSize?.height} width={crossSectionSize?.width}>
      <div style={{position: 'relative', width:'100%', height:'100%', padding:0, margin:0, fontSize:'14px'}}>
        <canvas id={"cross_section_chart"}></canvas>
      </div>
    </div>
  );

}

let parsedAtcPlan = {};
let etd;
const parseAtcPlan = (json) => {

  if(Object.keys(json).length === 0) {
    parsedAtcPlan = {};
    return;
  }

  // ルート
  const route = [];
  const routeWithAirway = [];
  // dep
  const dep = json.dep;
  route.push([dep.icao, dep.lat, dep.lon, 0, null, 0]);
  // 間のFIX
  let cumulativeTime = createRoute(json.route, route, 0, routeWithAirway);
  // arr
  const arr = json.arr;
  cumulativeTime += arr.time;
  const tmpLastFix = route[route.length -1];
  if(arr.icao === tmpLastFix[0] && arr.lat === tmpLastFix[1] && arr.lon === tmpLastFix[2]) {
    route.pop();
  }
  route.push([arr.icao, arr.lat, arr.lon, 0, null, cumulativeTime]);
  parsedAtcPlan["MainRoute"] = route;
  parsedAtcPlan["MainRouteType"] = "plan";
  parsedAtcPlan["routeWithAirway"] = routeWithAirway;

  // ETD
  if(json.dof) {
    etd = parseUTCDate(`${json.dof} ${dep.etd}00`);
  } else {
    etd = new Date();
    const depTime = parseInt(dep.etd.substr(0, 2));
    const depMin = parseInt(dep.etd.substr(2));
    if(depTime < etd.getUTCHours() || (depTime === etd.getUTCHours() && depMin < etd.getUTCMinutes())) {
      etd.setUTCDate(etd.getUTCDate() + 1);
    }
    etd.setUTCHours(depTime, depMin, 0);
  }
  
  const formattedEtd = formatUTCDate(etd);
  parsedAtcPlan["ETD"] = formattedEtd;

  // ETA
  parsedAtcPlan["ETA"] = formatUTCDate(new Date(etd.getTime() + cumulativeTime * 1000));;

  calcDistAndFl(route);

  const iconDom = document.getElementById('airplaneImage');
  if(isReverseRoute(dep.lon, arr.lon)) {
    iconDom.style.transform = 'scaleX(-1)';
  } else {
    iconDom.style.transform = null;
  }
}

const createRoute = (data, route, cumulativeTime = 0, routeWithAirway, airwayName, counter = 0) => {
  for(let i = 0; i < data.length; i++) {
    const record = data[i];
    if(record.hasOwnProperty('airway')) {
      cumulativeTime = createRoute(record.airway, route, cumulativeTime, routeWithAirway, record.airwayName, counter++);
    } else if (route[route.length - 1][0] !== record.name) {
      cumulativeTime += record.time;
      route.push([record.name, record.lat, record.lon, record.level, null, cumulativeTime]);
      routeWithAirway.push([record.name, record.lat, record.lon, airwayName, counter]);
    } else if(airwayName){
      routeWithAirway.push([record.name, record.lat, record.lon, airwayName, counter]);
    }
  }
  return cumulativeTime;
}

/**
 * 断面図をリサイズする
 */
const resizeCrossSection = () => {
  csController.resize();
  updateAirPlaneIcon();
}

/**
 * CrossSection.Controller
 */
let csController;
/**
 * 断面図左ルーラーの幅
 */
let leftRulerWidth;
/**
 * 断面図右ルーラーの幅
 */
let rightRulerWidth;
/**
 * 断面図下部マージン
 */
 let bottomMargin;
 /**
  * 断面図上部マージン
  */
 let topMargin;
 /**
 * 強調線の幅
 */
let highlightStrokeWidth;
 
/**
 * 断面図の初期化処理
 */
const initCrossSection = async (onMouseOver, onMouseOut, setRevision) => {
  crossContents[ModelUKMET.NAME] = new ModelUKMET(setRevision);
  crossContents[SatCld.NAME] = new SatCld(setRevision);
  crossContents[ASCScale.NAME] = new ASCScale(setRevision);
  crossContents[ACOS.NAME] = new ACOS(setRevision);

  const canvas = document.getElementById('cross_section_chart');

  if (window.Worker && canvas.transferControlToOffscreen) {
    //OffscreenCanvasに対応しているのでWeb Workerを使い別スレッドで断面図を描画する
    workerMode = true;
    const worker = new Worker('cs-worker-1.2.13.js');
    const wop = WrapOption();
    const wrapOption = {
      conf_path: "pri/conf/data",
      ascscale_img_path: null,
      option: {
        wrap_url: wop.wrap_url,
        expo_map_url: wop.expo_map_url,
        expo_tile_sat_url: wop.expo_tile_sat_url,
        expo_tile_gpv_url: wop.expo_tile_gpv_url
      }
    };
    csController = new CrossSection.CrossSectionControllerProxy(worker, canvas, wrapOption);
  } else {
    //従来のシングルスレッドで断面図を描画
    csController = new CrossSection.Controller('cross-section');
  }

  csController.addEventListenerOnMouseOver(onMouseOver);
  csController.addEventListenerOnMouseOut(onMouseOut);
  const config = CrossSectionConf;
  leftRulerWidth = config.left_ruler_width ? config.left_ruler_width : 40.0;
  rightRulerWidth = config.right_ruler_width ? config.right_ruler_width : 40.0;
  bottomMargin = config.bottom_ruler_height ? config.bottom_ruler_height : 50.0;
  topMargin = config.top_margin ? config.top_margin : 60.0;
  topMargin -= 5.0; // 断面図のコンテンツがtop_marginより少し上まではみ出して描画されるため調整
  highlightStrokeWidth = config.plan_route_line_width ? config.plan_route_line_width : 1;
  csController.initAppearance(config);
  csController.boundsChanged(boundsChange);
}

/**
 * 断面図モジュールにコンテンツを設定する
 */
const setObjects = async () => {
  for await (let contentsName of Object.keys(crossContents)) {
    let contents = crossContents[contentsName];
    if(!contents.getDataHandler() && contents.visible) {
      await contents.addObject();
      if (workerMode) {
        await csController.setObject(contents.getDataName(), contents.getDataReference()._data.conf);
      } else {
        csController.setObject(contents.getDataName(), contents.getDataHandler());
      }
    } 
  }
}

/**
 * 一つ前に表示していた断面図に設定したオプション
 */
let cacheOpt;

/**
 * ルートIDに設定するカウンター
 */
let routeIdCount = 0;
let _fpMode = false;
/**
 * 断面図を更新する
 */
const updateCrossSection = async () => {
  await setObjects();
  const opt = await getOpt(_fpMode);
  const optJson = sortObjectAndConvertToJSON(opt);
  const cacheOptJson = cacheOpt ? sortObjectAndConvertToJSON(cacheOpt) : null;
  if((cacheOptJson !== optJson && Object.keys(opt).length) || (Object.keys(opt).length === 1 && opt["Color"])) {
    const routeId = getRouteID(opt);
    cacheOpt = deepCopy(opt);
    opt["RouteID"] = routeId;
    if(opt["MainRoute"]) {
      hiddenProgress(false);
    }
    csController.update(opt, updateComplete, updateErr);
  } else {
    updateAirPlaneIcon();
  }
}

/**
 * 断面図に渡すオプションのオブジェクトをソートしてJsonに変換する
 * @param {Object} opt 断面図モジュールに渡すオプション
 * @returns String
 */
const sortObjectAndConvertToJSON = (opt) => {
  const sorted = Object.entries(opt).sort();
  for(let i in sorted){
    const val = sorted[i][1];
    if(!Array.isArray(val) && typeof val === "object"){
        sorted[i][1] = sortObjectAndConvertToJSON(val);
    }
  }
  return JSON.stringify(sorted);
}

/**
 * 断面図に渡すオプションRouteIDを返却する。
 * ルートに変更がある場合は新しいRouteIDを払い出す。
 * @param {Object} opt 断面図モジュールに渡すオプション
 * @returns String
 */
const getRouteID = (opt) => {
  if (cacheOpt && opt 
    && isEqualObject(cacheOpt.ETD, opt.ETD)
    && isEqualObject(cacheOpt.ETA, opt.ETA)
    && isEqualObject(cacheOpt.MainRoute, opt.MainRoute)
    && isEqualObject(cacheOpt.MainRouteType, opt.MainRouteType)) {
    return routeIdCount;
  }
  return ++routeIdCount;
}

/**
 * 描画完了時のCallback関数
 */
const updateComplete = () => {
  console.log("crossSection draw Complete");
  updateAirPlaneIcon();
  for(const key of Object.keys(crossContents)) {
    crossContents[key].afterDraw();
  }
  hiddenProgress(true);
}

/**
 * 飛行機アイコンをの位置を設定する
 */
const updateAirPlaneIcon = async () => {
  const iconDom = document.getElementById('airplaneIcon');
  if(!iconDom) {
    return;
  }
  if(!parsedAtcPlan.MainRoute || parsedAtcPlan.MainRoute.length === 0) {
    iconDom.style.display = 'none';
    return;
  }
  let left;
  let top;

  const alt = calcAltitudeBySec(_elapsedTime);
  if (workerMode) {
    // Web Workerとのやり取りの回数削減のためXY軸座標を同時に取得
    [left, top] = await csController.getXYPosition(_elapsedTime, alt / 100.0);
    left -= xOffset;
    top -= yOffset;
  } else {
    // X軸座標取得
    left = csController.getXTimePosition(_elapsedTime) - xOffset;
    // Y軸座標取得
    top = csController.getYPosition(alt / 100.0) - yOffset;
  }

  const cs = document.getElementById('cross-section');
  if(left + xOffset < leftRulerWidth || cs.clientWidth - rightRulerWidth - xOffset < left) {
    iconDom.style.display = 'none';
  } else if(top + yOffset < topMargin || cs.clientHeight - bottomMargin - yOffset < top) {
    iconDom.style.display = 'none';
  } else {
    iconDom.style.display = 'block';
  }
  iconDom.style.left = `${left}px`;
  iconDom.style.top = `${top}px`;
}

const calcAltitudeBySec = paramSec => {

  let prevData;
  for(let i = 0; i < parsedAtcPlan.MainRoute.length; i++) {
    const data = parsedAtcPlan.MainRoute[i];
    const dataSec = data[5];
    if(dataSec === paramSec) {
      return data[3];
    } else if(dataSec < paramSec && i !== parsedAtcPlan.MainRoute.length -1) {
      prevData = data;
    } else {
      const proportion = (paramSec - prevData[5]) / (data[5] - prevData[5]);
      let alt = prevData[3] + (data[3] - prevData[3]) * proportion;
      if(alt < 0) {
        alt = 0;
      }
      return alt;
    }
  }
}

const calcDistAndFl = (route) => {
  const routeLength = route.length;

  let prevData;
  let totalDist = 0;
  for(const currentData of route) {
    if(!prevData) {
      currentData[4] = totalDist;
      prevData = currentData;
      continue;
    }
    const dist = CrossSection.Util.routeDistance(prevData[1], prevData[2], currentData[1], currentData[2]);
    currentData[4] = (totalDist += dist);
    prevData = currentData;
  }

  for(let i = 1; i < routeLength; i++) {
    let fl = route[i][3];
    if ( fl <= 0 ) {
      let pd = 0;
      let pl = 0;
      for ( let j = i-1 ; j >= 0 ; j-- ) {
        if ( route[j][3] !== 0 ) {
          pd = parseFloat(route[j][4]);
          pl = parseFloat(route[j][3]);
          break;
        }
      }
      let nd = parseFloat(route[routeLength-1][4]);
      let nl = parseFloat(route[routeLength-1][3]);
      for ( let j = i+1 ; j < routeLength ; j++ ) {
        if ( route[j][3] !== 0 ) {
          nd = parseFloat(route[j][4]);
          nl = parseFloat(route[j][3]);
          break;
        }
      }
      fl = CrossSection.Util.interpolate(pd, nd, route[i][4], pl, nl);
      route[i][3] = fl;
    }
  }
}

/**
 * 描画失敗時のCallback関数
 */
const updateErr = err => {
  console.log("crossSection draw Error");
  console.log(err);
  hiddenProgress(true);
}

/**
 * 描画しているコンテンツの画角変更処理を実装する
 */
const boundsChange = () => {
  updateAirPlaneIcon();
}

/**
 * 断面図描画オプションを取得する
 */
let _color = "black";
const getOpt = async fpMode => {
  const opt = getRoute();
  opt["Color"] = _color;
  if(etd) {
    const validTime = formatUTCDate(new Date(etd.getTime() + _elapsedTimeForWeather * 1000));
    for await (let contentsName of Object.keys(crossContents)) {
      let contents = crossContents[contentsName];
      if(contents.visible && contents.isReady()) {
        Object.assign(opt, await contents.getOpt(validTime, fpMode));
      }
    }
  }
  return opt;
}

/**
 * ルート情報を取得する
 */
const getRoute = () => {
  return JSON.parse(JSON.stringify(parsedAtcPlan));
}

const setCrossSectionColor = color => {
  _color = color;
  updateCrossSection()
}

const createEmphasizeOption = async (highlightPoints) => {

  const cs = document.getElementById('cross-section');
  const rightLimit = cs.clientWidth - rightRulerWidth;
  const bottomLimit = cs.clientHeight - bottomMargin;
  const fixes = [];
  for(const point of highlightPoints) {
    if(point.source === "crosssection") {
      return {fixes: []};
    }
    for (const route of parsedAtcPlan.MainRoute) {
      if(route[0] === point.name && route[1] === point.lat && route[2] === point.lon) {
        let top, left;
        if (workerMode) {
          [left, top] = await csController.getXYPosition(route[5], route[3] / 100.0);
        } else {
          top = csController.getYPosition(route[3] / 100.0);
          left = csController.getXTimePosition(route[5]);
        }

        let fixVisible = true;
        if(left < leftRulerWidth || rightLimit < left) {
          fixVisible = false;
        } else if(top < topMargin || bottomLimit < top) {
          fixVisible = false;
        }
        fixes.push({top: top, left: left, fixVisible:fixVisible});
      }
    }
  }
  return {
    fixes: fixes,
    rulerWidth: {
      left: leftRulerWidth,
      right: rightRulerWidth,
      top: topMargin,
      bottom: bottomMargin
    },
    strokeWidth: highlightStrokeWidth
  };
}

export default FPECrossSection;